import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Checkbox, List, ListItem, ListItemText } from '@mui/material';
import { Crop, X as XIcon, ChevronLeft } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import "../styles/selectPlaces.css";
import { compressImage, compressVideo } from '../utils/mediaCompressor';
import Cropper from 'react-easy-crop';
import { getPostExistingTripItems, clearPostExistingTripItems, setPostExistingTripItems } from '../store/postExistingTripStore';
import PlaceOptionsDialog from './PlaceOptionsDialog';

export default function PostExistingTripPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(() => getPostExistingTripItems());
  const [groupedByPlace, setGroupedByPlace] = useState({});
  // Per-place metadata (caption, price, rating, etc.)
  const [placeDetails, setPlaceDetails] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  // Tagging & user selection (borrowed from TrippinPlaceSelect)
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [closeFriends, setCloseFriends] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [taggingPlaceName, setTaggingPlaceName] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const companionshipOptions = ['Alone', 'Couple', 'Couples', 'Family', 'Friends'];
  const currencyOptions = [
    'USD','EUR','GBP','JPY','CAD','AUD','CHF','CNY','SEK','INR','BRL','MXN','ZAR','RUB','AED'
  ];

  // Crop state
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropItemIndex, setCropItemIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const cropPixelsRef = useRef(null);

  // Place selection dialog (reuse SeeContent's place picker UI)
  const [placePickerOpen, setPlacePickerOpen] = useState(false);
  const [placePickerCtx, setPlacePickerCtx] = useState(null);

  useEffect(() => {
    if (!items || items.length === 0) {
      // No items in store, go back
      navigate(-1);
      return;
    }
    const groups = items.reduce((acc, it) => {
      const name = it?.rg?.place?.name || it?.rg?.locality?.city || it?.rg?.locality?.state || 'Unknown';
      (acc[name] ||= []).push(it);
      return acc;
    }, {});
    setGroupedByPlace(groups);
    // Initialize placeDetails entries if missing
    setPlaceDetails(prev => {
      const next = { ...prev };
      Object.keys(groups).forEach(p => {
        if (!next[p]) {
          next[p] = { caption: '', price: '', rating: 0, currency: 'USD', companionship: '', review: '', tags: [] };
        }
      });
      // Remove metadata for places no longer present
      Object.keys(next).forEach(p => { if (!groups[p]) delete next[p]; });
      return next;
    });
  }, [items, navigate]);

  // Keep the in-memory store in sync when we mutate items (e.g. change place).
  useEffect(() => {
    try { setPostExistingTripItems(items); } catch (_) { /* ignore */ }
  }, [items]);

  // Fetch close friends (taggable users) once
  useEffect(() => {
    const fetchCloseUsers = async () => {
      try {
        const response = await axiosInstance.get('/flashes/close-users');
        setCloseFriends(response.data.users || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchCloseUsers();
  }, []);

  const canPost = useMemo(() => items && items.length > 0 && !uploading, [items, uploading]);

  const postExistingTrip = async () => {
    if (!items?.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: items.length });
    try {
      const postedPlaces = new Set();
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        let blob;
        if (it.type === 'image') {
          if (it.croppedBlob) {
            blob = it.croppedBlob;
          } else {
            const { uri } = await compressImage(it.previewUrl, { maxWidth: 1920, maxHeight: 1920, quality: 0.8, format: 'JPEG' });
            blob = await fetch(uri).then(r => r.blob());
          }
        } else {
          const { uri } = await compressVideo(it.previewUrl, { maxSize: 1080, bitrate: 2_000_000, keepAudio: true });
          blob = await fetch(uri).then(r => r.blob());
        }
        const headers = i === 0 ? { 'Start-Trippin': 'true' } : undefined;
        const gen = await axiosInstance.post('/trippin/generate-upload-url', { isVideo: it.type === 'video', contentType: blob.type }, { headers });
        const { uploadUrl, fileName } = gen.data || {};
        if (uploadUrl) {
          const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': blob.type }, body: blob });
          if (!put.ok) {
            const t = await put.text().catch(() => '');
            throw new Error(`Upload failed ${put.status}: ${t.slice(0,120)}`);
          }
        }
        // Determine place name for current item (same logic as grouping)
        const placeName = it?.rg?.place?.name || it?.rg?.locality?.city || it?.rg?.locality?.state || 'Unknown';
        const placeMeta = placeDetails[placeName] || {};
        const includeMeta = !postedPlaces.has(placeName); // send metadata only once per place
        await axiosInstance.post('/trippin/save-metadata', {
          fileName,
          latitude: it.lat,
          longitude: it.lon,
          cachedReverseGeocode: it.rg || undefined,
          caption: includeMeta && placeMeta.caption ? placeMeta.caption : undefined,
          price: includeMeta && placeMeta.price ? Number(placeMeta.price) : undefined,
          rating: includeMeta && (placeMeta.review || placeMeta.rating) ? Number(placeMeta.review || placeMeta.rating) : undefined,
          currency: includeMeta && placeMeta.currency ? placeMeta.currency : undefined,
          companionship: includeMeta && placeMeta.companionship ? placeMeta.companionship : undefined,
          tags: includeMeta && placeMeta.tags && placeMeta.tags.length ? placeMeta.tags : undefined,
        }, { headers });
        if (includeMeta) postedPlaces.add(placeName);
        setUploadProgress(prev => ({ ...prev, done: prev.done + 1 }));
      }
      clearPostExistingTripItems();
      alert('Your media were posted to a new trippin!');
      navigate('/yourtrippins');
    } catch (e) {
      console.error('postExistingTrip error', e);
      alert('Failed to post trip. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const formatLocality = (loc) => {
    if (!loc) return 'Unknown area';
    const parts = [loc.area, loc.village, loc.city, loc.state, loc.country].filter(Boolean);
    return parts.join(' · ');
  };

  const openTagDialog = (placeName) => {
    setTaggingPlaceName(placeName);
    const existingTags = placeDetails[placeName]?.tags || [];
    setSelectedUsers(existingTags);
    setIsTagDialogOpen(true);
  };

  const openPlacePicker = (placeName, groupItems) => {
    const first = groupItems?.[0];
    const lat = first?.lat;
    const lon = first?.lon;
    const rg = first?.rg || null;
    setPlacePickerCtx({ placeName, groupItems, lat, lon, rg });
    setPlacePickerOpen(true);
  };

  const applySelectedPlaceToGroup = ({ reverseGeocodeResult }) => {
    if (!placePickerCtx?.placeName || !reverseGeocodeResult?.place) return;
    const oldName = placePickerCtx.placeName;
    const newName = reverseGeocodeResult.place.name || oldName;

    setItems((prev) => {
      const next = prev.map((it) => {
        const currentName = it?.rg?.place?.name || it?.rg?.locality?.city || it?.rg?.locality?.state || 'Unknown';
        if (currentName !== oldName) return it;
        return { ...it, rg: reverseGeocodeResult };
      });
      return next;
    });

    // Move per-place metadata to the new group key (if the name changed).
    setPlaceDetails((prev) => {
      if (oldName === newName) return prev;
      const next = { ...prev };
      const oldMeta = next[oldName];
      if (!next[newName]) {
        next[newName] = oldMeta || { caption: '', price: '', rating: 0, currency: 'USD', companionship: '', review: '', tags: [] };
      }
      delete next[oldName];
      return next;
    });
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (!term) { setFilteredUsers([]); return; }
    setLoadingUsers(true);
    try {
      const response = await axiosInstance.get('/flashes/search-users', { params: { query: term } });
      setFilteredUsers(response.data.users || []);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const exists = prev.find(u => u.user_id === user.user_id);
      if (exists) return prev.filter(u => u.user_id !== user.user_id);
      return [...prev, user];
    });
  };

  const confirmTags = () => {
    if (!taggingPlaceName) return;
    setPlaceDetails(prev => ({
      ...prev,
      [taggingPlaceName]: {
        ...(prev[taggingPlaceName] || {}),
        tags: selectedUsers.map(u => ({ user_id: u.user_id, username: u.username }))
      }
    }));
    setIsTagDialogOpen(false);
    setTaggingPlaceName(null);
    setSelectedUsers([]);
  };

  return (
    <div className="tailwind min-h-screen w-full flex flex-col pt-12 p-4 gap-5 text-white">
      <div className="relative flex items-center justify-center py-1">
        <button
          type="button"
          onClick={() => { clearPostExistingTripItems(); navigate(-1); }}
          aria-label="Go back"
          className="absolute left-0 inline-flex items-center gap-1 px-1"
        >
          <ChevronLeft size={32} />
        </button>
        <div className="font-semibold text-lg tracking-wide">Post Existing Trippin</div>
      </div>

      <div className="flex flex-col gap-4">
        {Object.entries(groupedByPlace).map(([placeName, groupItems]) => {
          const headerRG = groupItems[0]?.rg;
          const place = headerRG?.place || null;
          const locality = headerRG?.locality || null;
          return (
            <div key={placeName} className="rounded-lg bg-smallDarkCard p-3">
              {/* Place header with details */}
              <div className="mb-3">
                <button
                  type="button"
                  onClick={() => openPlacePicker(placeName, groupItems)}
                  className="w-full text-left"
                  aria-label="Change place"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-xl font-semibold">{place?.name || placeName || 'Unknown place'}</div>
                    <div className="text-xs text-gray-400">(tap to change)</div>
                  </div>
                  <div className="mt-1 text-sm text-gray-300">{formatLocality(locality)}</div>
                  <div className="mt-1 text-xs">
                    {groupItems.length} item{groupItems.length > 1 ? 's' : ''}
                  </div>
                </button>
                {/* Per-place inputs */}
                <div className="mt-3 flex flex-col gap-2">
                  <div className="form-row">
                    <label className="form-label">Caption:</label>
                    <input
                      type="text"
                      value={placeDetails[placeName]?.caption || ''}
                      onChange={(e) => setPlaceDetails(prev => ({ ...prev, [placeName]: { ...prev[placeName], caption: e.target.value } }))}
                    />
                  </div>

                  <div className="form-row">
                    <label className="form-label">Tags:</label>
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        backgroundColor: '#48c9b0 !important',
                        boxShadow: 'none',
                        color: '#101010 !important',
                        padding: '4px 8px !important',
                        borderRadius: '6px',
                      }}
                      onClick={() => openTagDialog(placeName)}
                    >
                      Add Users
                    </Button>
                  </div>

                  {placeDetails[placeName]?.tags?.length > 0 && (
                    <div className="tagged-users">
                      Tagged Users: {placeDetails[placeName].tags.map(t => t.username).join(', ')}
                    </div>
                  )}

                  <div className="form-row">
                    <label className="form-label">Companionship:</label>
                    <select
                      value={placeDetails[placeName]?.companionship || ''}
                      onChange={(e) => setPlaceDetails(prev => ({ ...prev, [placeName]: { ...prev[placeName], companionship: e.target.value } }))}
                    >
                      <option value="">-- Select --</option>
                      {companionshipOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-row">
                    <label className="form-label">Review (1-5):</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={placeDetails[placeName]?.review || ''}
                      onChange={(e) => setPlaceDetails(prev => ({ ...prev, [placeName]: { ...prev[placeName], review: e.target.value } }))}
                    />
                  </div>

                  <div className="form-row">
                    <label className="form-label">Price:</label>
                    <input
                      type="number"
                      min="0"
                      value={placeDetails[placeName]?.price || ''}
                      onChange={(e) => setPlaceDetails(prev => ({ ...prev, [placeName]: { ...prev[placeName], price: e.target.value } }))}
                    />
                    <select
                      value={placeDetails[placeName]?.currency || 'USD'}
                      onChange={(e) => setPlaceDetails(prev => ({ ...prev, [placeName]: { ...prev[placeName], currency: e.target.value } }))}
                    >
                      {currencyOptions.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {groupItems.map((it) => {
                  const globalIndex = items.indexOf(it);
                  return (
                    <div key={globalIndex} className="relative group overflow-hidden rounded aspect-[3/4] bg-black">
                      {it.type === 'video' ? (
                        <video src={it.previewUrl} className="w-full h-full object-cover" muted playsInline loop />
                      ) : (
                        <img src={it.previewUrl} alt="media" className="w-full h-full object-cover" />
                      )}
                      {/* Remove icon button */}
                      <button
                        type="button"
                        aria-label="Remove media"
                        onClick={() => setItems(prev => prev.filter((_, i) => i !== globalIndex))}
                        className="absolute top-1 right-1 bg-likeColor text-white p-1 rounded-full flex items-center justify-center"
                      >
                        <XIcon size={14} />
                      </button>
                      {/* Crop icon button */}
                      {it.type === 'image' && (
                        <button
                          type="button"
                          aria-label="Crop image"
                          className="absolute bottom-1 right-1 bg-black text-white p-1 rounded flex items-center justify-center"
                          onClick={() => { setCropItemIndex(globalIndex); setCropDialogOpen(true); }}
                        >
                          <Crop size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <Button
          variant="contained"
          onClick={postExistingTrip}
          disabled={!canPost}
          fullWidth
          sx={{
            backgroundColor: '#48c9b0 !important',
            color: '#101010 !important',
            boxShadow: 'none',
            py: '0.5rem !important',
            fontWeight: 600,
            fontSize: '1.125rem !important',
            '&.Mui-disabled': { backgroundColor: '#48c9b0', color: '#101010', opacity: 0.5 }
          }}
        >
          {uploading ? `Posting ${uploadProgress.done}/${uploadProgress.total}…` : 'Post'}
        </Button>
      </div>

      {cropDialogOpen && cropItemIndex != null && items[cropItemIndex] && (
        <Dialog
          open={cropDialogOpen}
          onClose={() => setCropDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          slotProps={{
            paper: {
              sx: {
                borderRadius: '12px',
                backgroundColor: 'var(--small-dark-card)',
                color: 'white',
                border: '1px solid var(--small-dark-card)',
              },
            },
          }}
        >
          <DialogTitle sx={{ color: '#fff', textAlign: 'center' }}>Crop image (3:4)</DialogTitle>
          <DialogContent>
            <div className="relative w-full" style={{ height: 400 }}>
              <Cropper
                image={items[cropItemIndex].previewUrl}
                crop={crop}
                zoom={zoom}
                aspect={3/4}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, areaPixels) => { cropPixelsRef.current = areaPixels; }}
              />
            </div>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'space-evenly', paddingBottom: '32px' }}>
            <Button
              onClick={() => setCropDialogOpen(false)}
              sx={{
                textTransform: 'none',
                fontSize: '16px',
                padding: '5px 16px',
                backgroundColor: 'var(--liked-red)',
                color: 'white',
                borderRadius: '5px',
              }}
            >
              Cancel
            </Button>
            <Button onClick={async () => {
              try {
                const area = cropPixelsRef.current;
                if (!area) { setCropDialogOpen(false); return; }
                const blob = await getCroppedBlob(items[cropItemIndex].previewUrl, area, 'image/jpeg', 0.9);
                setItems(prev => prev.map((x, i) => i === cropItemIndex ? { ...x, croppedBlob: blob } : x));
                setCropDialogOpen(false);
              } catch (e) {
                console.error('crop failed', e);
                setCropDialogOpen(false);
              }
            }}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: '16px',
                padding: '5px 16px',
                backgroundColor: 'var(--main-light-color)',
                color: 'var(--small-dark-card)',
                borderRadius: '5px',
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Tag dialog */}
      <Dialog
        open={isTagDialogOpen}
        onClose={() => setIsTagDialogOpen(false)}
        slotProps={{
          paper: { sx: { borderRadius: '12px', padding: '14px', maxWidth: '400px', width: '100%', backgroundColor: '#1f1f1f', color: 'white', border: '1px solid #1f1f1f' } }
        }}
      >
        <DialogTitle>Select users:</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{
              backgroundColor: '#101010',
              color: 'white',
              borderRadius: '8px',
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#101010' },
                '&.Mui-focused fieldset': { borderColor: '#48c9b0', borderWidth: '2px' }
              },
              '& .MuiInputBase-input::placeholder': { color: '#d1d5db', opacity: 1 }
            }}
          />
          <List sx={{ maxHeight: '250px', overflowY: 'auto', mt: 1 }}>
            {(searchTerm ? filteredUsers : closeFriends).map(user => (
              <ListItem
                key={user.user_id}
                button
                onClick={() => toggleUserSelection(user)}
                sx={{ padding: 0, borderRadius: '6px' }}
              >
                <Checkbox
                  checked={selectedUsers.some(u => u.user_id === user.user_id)}
                  sx={{ color: '#d1d5db', '&.Mui-checked': { color: '#48c9b0' } }}
                />
                <ListItemText primary={user.username} />
              </ListItem>
            ))}
            {loadingUsers && (
              <ListItem sx={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <Button
            onClick={confirmTags}
            sx={{ textTransform: 'none', fontSize: '16px', padding: '5px 16px', backgroundColor: '#48c9b0', color: '#1f1f1f', borderRadius: '5px' }}
          >Confirm</Button>
          <Button
            onClick={() => setIsTagDialogOpen(false)}
            sx={{ textTransform: 'none', fontSize: '16px', padding: '5px 16px', backgroundColor: '#ff3040', color: 'white', borderRadius: '5px' }}
          >Cancel</Button>
        </DialogActions>
      </Dialog>

      <PlaceOptionsDialog
        open={placePickerOpen}
        onClose={() => { setPlacePickerOpen(false); setPlacePickerCtx(null); }}
        latitude={placePickerCtx?.lat}
        longitude={placePickerCtx?.lon}
        initialReverseGeocode={placePickerCtx?.rg || null}
        initialSelectedId={placePickerCtx?.rg?.place?.id || null}
        title="Select Place"
        subtitle="Multiple places found at this location. Please select the most appropriate one:"
        onConfirm={(payload) => {
          applySelectedPlaceToGroup(payload);
          setPlacePickerOpen(false);
          setPlacePickerCtx(null);
        }}
      />
    </div>
  );
}

async function getCroppedBlob(imageSrc, crop, mime = 'image/jpeg', quality = 0.92) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), mime, quality));
}
